import { createContext, useContext, useMemo, FC, PropsWithChildren } from "react";

import { FormBackgroundTheme, isAnimatedBackgroundTheme, isStaticShapesTheme } from "@simplyk/common";

import { ThemedBackgroundShapes } from "./ThemedBackgroundShapes";
import { useBackgroundShapesState } from "./useBackgroundShapesState";

import { FormCanvasDonationBackground } from "@/canvas/components/BackgroundAnimations/FormCanvasDonationBackground";
import { useDynamicThemeAwareVariables } from "@/common/hooks/useDynamicThemeAwareVariables";

interface ShapesContextType {
  rotate: (shapeId?: string) => void;
  drop: (shapeId?: string) => void;
  reverseRotate: (shapeId?: string) => void;
  up: (shapeId?: string) => void;
  backgroundColor: string;
  shapesBackgroundColor: string;
}

const noop = () => {};

const NOOP_CONTEXT: ShapesContextType = {
  rotate: noop,
  drop: noop,
  reverseRotate: noop,
  up: noop,
  backgroundColor: "",
  shapesBackgroundColor: "",
};

const ShapesContext = createContext<ShapesContextType>(NOOP_CONTEXT);

export const useShapes = (): ShapesContextType => {
  return useContext(ShapesContext);
};

interface ShapesProviderProps {
  backgroundTheme?: FormBackgroundTheme | null;
  brandColorHex?: string;
  /** Override auto-detection of whether to show static SVG shapes. Used by FormPostPaymentPageLayout to disable abstract shapes (handled via inline CSS background-images there). */
  showStaticShapes?: boolean;
}

export const ShapesProvider: FC<PropsWithChildren<ShapesProviderProps>> = ({
  children,
  backgroundTheme = null,
  brandColorHex,
  showStaticShapes,
}) => {
  const { rotate, drop, reverseRotate, up, shapesData } = useBackgroundShapesState(backgroundTheme);

  const isAbstract = !backgroundTheme || backgroundTheme === FormBackgroundTheme.Abstract;
  const hasAnimatedBg = Boolean(backgroundTheme && isAnimatedBackgroundTheme(backgroundTheme));
  const hasStaticShapes = Boolean(backgroundTheme && isStaticShapesTheme(backgroundTheme));

  const shapesEnabled = showStaticShapes ?? hasStaticShapes;

  const backgroundTexture =
    hasAnimatedBg && backgroundTheme && brandColorHex ? (
      <FormCanvasDonationBackground kind={backgroundTheme} brandColorHex={brandColorHex} />
    ) : undefined;

  const { backgroundColor, shapesBackgroundColor } = useDynamicThemeAwareVariables(({ formsPalette, mode }) => ({
    backgroundColor: mode === "light" ? formsPalette.surface.supershy : formsPalette.surface.intense,
    shapesBackgroundColor: mode === "light" ? formsPalette.surface.quiet : formsPalette.surface.moderate,
  }));

  const contextValue: ShapesContextType = useMemo(
    () =>
      isAbstract
        ? { rotate, drop, reverseRotate, up, backgroundColor, shapesBackgroundColor }
        : { ...NOOP_CONTEXT, backgroundColor, shapesBackgroundColor },
    [isAbstract, rotate, drop, reverseRotate, up, backgroundColor, shapesBackgroundColor]
  );

  return (
    <ShapesContext.Provider value={contextValue}>
      <ThemedBackgroundShapes
        {...shapesData}
        theme={backgroundTheme}
        showShapes={shapesEnabled}
        backgroundTexture={backgroundTexture}
        backgroundColor={backgroundColor}
        shapesBackgroundColor={shapesBackgroundColor}
      />
      {children}
    </ShapesContext.Provider>
  );
};
